import { CallTimingSettings } from "./CallDesk";
import { RadioAudioControls } from "./RadioPanel";
import type { RadioController } from "../audio/useRadioAudio";
import { useState } from "react";
import {
  ArrowDownToLine,
  Coins,
  LogOut,
  RotateCcw,
  Upload,
  UserRound,
} from "lucide-react";
import type { SimState } from "../domain/model";
import type { Perform } from "./ResourceManager";
import { request } from "../api";
import type { Player } from "../domain/player";
export default function Settings({
  state,
  audio,
  act,
  enabled,
  provider,
  onState,
  notify,
  player,
  onPlayer,
  onLogout,
}: {
  state: SimState;
  audio: RadioController;
  act: Perform;
  enabled: boolean;
  provider: string;
  onState: (s: SimState) => void;
  notify: (s: string) => void;
  player: Player;
  onPlayer: (player: Player) => void;
  onLogout: () => Promise<void>;
}) {
  const [importData, setImportData] = useState<unknown>(null),
    [fileName, setFileName] = useState(""),
    [confirmReset, setConfirmReset] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  async function replace(path: string, data: unknown) {
    setBusy(true);
    setError("");
    try {
      const result = await request<{ state: SimState; player?: Player }>(
        path,
        data,
      );
      onState(result.state);
      if (result.player) onPlayer(result.player);
      setImportData(null);
      setConfirmReset(false);
      notify(
        path === "reset"
          ? "Spielstand zurückgesetzt. Bitte eine neue Leitstelle wählen."
          : "Spielstand importiert. Simulation ist pausiert.",
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="dialog-stack">
      <div className="account-card">
        <span className="account-avatar">
          <UserRound />
        </span>
        <span>
          <small>SPIELERKONTO</small>
          <b>{player.username}</b>
          <em>{player.email}</em>
        </span>
        <span className="account-credits">
          <Coins /> {player.credits.toLocaleString("de-DE")} Credits
        </span>
        <button className="button secondary" onClick={() => void onLogout()}>
          <LogOut size={15} /> Abmelden
        </button>
      </div>
      <label className="switch-row">
        <span>
          Automatische Notrufe
          <small>Eingehende Anrufe · Intervall unten einstellbar</small>
        </span>
        <input
          type="checkbox"
          checked={state.autoGenerate}
          onChange={(e) =>
            act({ type: "settings", autoGenerate: e.target.checked })
          }
          disabled={!enabled}
        />
      </label>
      <CallTimingSettings state={state} act={act} enabled={enabled} />
      <RadioAudioControls audio={audio} />
      <div className="settings-group">
        <h3>Spielstand sichern</h3>
        <p className="muted">
          Dein Spielstand wird auf dem Server gespeichert und steht auf deinen
          angemeldeten Geräten zur Verfügung. Ein Export erstellt eine
          zusätzliche Sicherung.
        </p>
        <a className="button secondary" href="/api/export" download>
          <ArrowDownToLine size={16} />
          Spielstand exportieren
        </a>
        <label className="file-field">
          <Upload size={16} />
          Spielstand importieren
          <input
            type="file"
            accept=".json,application/json"
            aria-label="Spielstand-Datei"
            disabled={!enabled || busy}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setError("");
              try {
                if (file.size > 8 * 1024 * 1024)
                  throw Error("Die Datei darf maximal 8 MB groß sein.");
                setImportData(JSON.parse(await file.text()));
                setFileName(file.name);
              } catch (err) {
                setImportData(null);
                setError((err as Error).message);
              }
            }}
          />
        </label>
        {importData !== null && (
          <div className="confirm-box">
            <p>
              <b>{fileName}</b> ersetzt den aktuellen Spielstand. Zuvor legt der
              Server eine zusätzliche Sicherung im Datenverzeichnis an.
            </p>
            <button
              className="button primary"
              disabled={!enabled || busy}
              onClick={() => replace("import", importData)}
            >
              Import bestätigen
            </button>
            <button className="text-button" onClick={() => setImportData(null)}>
              Abbrechen
            </button>
          </div>
        )}
      </div>
      <div className="settings-group">
        <h3>Persönlichen Spielstand zurücksetzen</h3>
        <p className="muted">
          Entfernt Credits, Leitstellenwahl, Einsätze und deinen Ausbau. Danach
          legst du die Leitstelle erneut fest und nimmst neue Notrufe entgegen.
        </p>
        {confirmReset ? (
          <div className="confirm-box">
            <p>Persönlichen Spielstand vollständig zurücksetzen?</p>
            <button
              className="button primary"
              disabled={!enabled || busy}
              onClick={() => replace("reset", {})}
            >
              Zurücksetzen bestätigen
            </button>
            <button
              className="text-button"
              onClick={() => setConfirmReset(false)}
            >
              Abbrechen
            </button>
          </div>
        ) : (
          <button
            className="button secondary"
            disabled={!enabled}
            onClick={() => setConfirmReset(true)}
          >
            <RotateCcw size={16} />
            Spielstand zurücksetzen
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}
      <p className="muted">
        Kartengrundlage: {provider}. Anfahrten sind Schätzungen. Die Simulation
        läuft weiter, solange der Server läuft; Pausieren hält die Zeit für alle
        Browserfenster an.
      </p>
    </div>
  );
}
